/**
 * Pure helpers for identifying bookable itinerary items and grouping them for
 * the booking surfaces: by type (flights / hotel stays) for the "book your trip"
 * sheet/modal, and by hotel stay for the full bookings list (web's bookings
 * tab, mobile's bookings screen). Both split hotels per stay (hotel + dates).
 */

import type { AirlineInfo } from '@elara/domain/models/schemas/flight-schemas';
import type { RoomSelection } from '@elara/domain/models/schemas/room-schemas';

import type { ItineraryItem } from '#models/ItineraryItemModel.js';

import { resolvePlaceEventType } from '#lib/place-category.js';

export function isRoomUnavailable(item: ItineraryItem): boolean {
  if (item.type !== 'room' || item.isCancelled) return false;
  // Server-denormalized verdict takes priority (avoids loading the RoomOption doc).
  if (item.availabilityState === 'unavailable') return true;
  if (!item.roomOption) return false;
  const meta = item.metadata as (RoomSelection & { selectedRateKey?: string }) | undefined;
  if (meta?.productCode) {
    return !item.roomOption.isRateAvailableByIdentity({ productCode: meta.productCode, occupancy: meta.occupancy });
  }
  if (meta?.selectedRateKey) {
    return !item.roomOption.isRateAvailable(meta.selectedRateKey);
  }
  return false;
}

export function isFlightUnavailable(item: ItineraryItem): boolean {
  if (item.type !== 'flight' || item.isCancelled) return false;
  if (item.availabilityState === 'unavailable') return true;
  if (!item.flightOption) return false;
  return !item.flightOption.isAvailable;
}

/** True when the item can still be sent to checkout (planned, live, not cancelled). Supplier-agnostic. */
export function isBookable(item: ItineraryItem): boolean {
  return item.isPlanned && !item.isCancelled && !isRoomUnavailable(item) && !isFlightUnavailable(item);
}

export interface BookableFlight {
  itemId: string;
  referenceId: string;
  title: string;
  airline?: AirlineInfo;
  departDate?: string;
  returnDate?: string;
}

/**
 * One hotel STAY still to book — a hotel plus one check-in/check-out pair. Two stays at the same
 * hotel on different dates are two groups: each is its own checkout, and the server refuses a
 * checkout whose rooms disagree on dates (`SubmitHotelBookingFlow`'s stay-date check, ELARA-1087).
 */
export interface BookableHotelGroup {
  /** Unique per stay (`hotelId|checkIn|checkOut`) — the row key; `hotelId` alone repeats across stays. */
  stayKey: string;
  hotelId: string;
  hotelName: string;
  /** Check-in date (YYYY-MM-DD) shared by every room in the group; undefined when the item has no start. */
  checkIn?: string;
  /** Check-out date (YYYY-MM-DD) shared by every room in the group; undefined when the item has no end. */
  checkOut?: string;
  /** The stay's human-readable date range, or undefined when either date is missing (never the model's `'-'`). */
  dateRange?: string;
  roomItemIds: string[];
  imageUrl?: string;
}

export interface BookableGroups {
  flights: BookableFlight[];
  hotels: BookableHotelGroup[];
}

/** The YYYY-MM-DD date part of an item's ISO date-time — what a stay's check-in/check-out is keyed on. */
function toStayDate(dateTime: string | null | undefined): string | undefined {
  return dateTime ? dateTime.slice(0, 10) : undefined;
}

/**
 * Partition a trip's itinerary items into bookable flights and hotel stays.
 * Deduplicates flights by referenceId (multiple passengers share one option)
 * and groups room items by STAY — hotel plus check-in/check-out dates, the
 * same split {@link groupRoomsByStay} gives the bookings list — sorted by
 * check-in. Grouping by hotel alone merged two stays at one hotel into a
 * single checkout the server then refused at Pay (ELARA-1087).
 */
export function buildBookableGroups(items: ItineraryItem[]): BookableGroups {
  const bookable = items.filter(isBookable);

  const flightsSeen = new Set<string>();
  const flights: BookableFlight[] = [];
  for (const item of bookable) {
    if (!item.isFlight || !item.referenceId || flightsSeen.has(item.referenceId)) continue;
    flightsSeen.add(item.referenceId);
    flights.push({
      itemId: item.id,
      referenceId: item.referenceId,
      title: item.displayTitle,
      airline: item.flightOption?.primaryAirline('outbound'),
      departDate: item.flightOption?.firstFlight('outbound')?.formattedDepartureDate,
      returnDate: item.flightOption?.firstFlight('return')?.formattedDepartureDate,
    });
  }

  const stayMap = new Map<string, BookableHotelGroup>();
  for (const item of bookable) {
    if (!item.isRoom) continue;
    // Ends at the item's own id rather than an 'unknown' placeholder, so two hotels whose docs haven't
    // loaded yet can't collapse into one bucket.
    const hotelId = item.hotel?.id ?? item.referenceId ?? item.id;
    const checkIn = toStayDate(item.startDateTime);
    const checkOut = toStayDate(item.endDateTime);
    const stayKey = `${hotelId}|${checkIn ?? ''}|${checkOut ?? ''}`;
    const existing = stayMap.get(stayKey);
    if (existing) {
      existing.roomItemIds.push(item.id);
      continue;
    }
    const dateRange = item.formattedDateRange;
    stayMap.set(stayKey, {
      stayKey,
      hotelId,
      hotelName: item.hotelName ?? item.displayTitle,
      checkIn,
      checkOut,
      // The model renders a missing date as '-'; surface that as absent so callers never print it.
      dateRange: dateRange && dateRange !== '-' ? dateRange : undefined,
      roomItemIds: [item.id],
      // Catalog hotel photo, else the Google Place attached to an externally-booked stay.
      imageUrl: item.stayImages[0]?.url,
    });
  }

  // toSorted is stable, so stays sharing a check-in keep itinerary order.
  const hotels = [...stayMap.values()].toSorted((a, b) => (a.checkIn ?? '').localeCompare(b.checkIn ?? ''));
  return { flights, hotels };
}

/**
 * How many things are left to book, as a guest would count them: every ROOM, not every hotel — a stay
 * with three unbooked rooms is three bookings to make, and calling it one under-reported the work. A
 * flight counts once per option (`buildBookableGroups` already dedupes the passengers sharing one).
 */
export function countBookableItems({ flights, hotels }: BookableGroups): number {
  return flights.length + hotels.reduce((total, hotel) => total + hotel.roomItemIds.length, 0);
}

/** The two buckets a non-flight/non-room itinerary item falls into on the booking surfaces. */
export type EventCategory = 'dining' | 'activity';

/**
 * True once every `place` item's referenced place doc has resolved. Category resolution depends on
 * that doc, so a list rendered before it lands would visibly reshuffle as the docs stream in
 * one-by-one — callers hold every place in `activity` until this is true (ELARA-836's reasoning,
 * applied to the bookings list).
 */
export function arePlacesHydrated(items: ItineraryItem[]): boolean {
  return items.every((item) => item.type !== 'place' || item.place != null);
}

/**
 * Which bucket a non-flight/non-room item belongs in: an eatery reads as Dining, everything else
 * (other places, activities, custom events) as Activities. Pass `placesHydrated` from
 * {@link arePlacesHydrated} — while false, a place stays in `activity` rather than guessing.
 *
 * Shared so web's bookings tab and the mobile bookings screen can't disagree about what counts as
 * dining; each still owns its own section labels and ordering.
 */
export function resolveEventCategory(item: ItineraryItem, placesHydrated: boolean): EventCategory {
  if (item.type !== 'place') return 'activity';
  const place = placesHydrated ? item.place : undefined;
  // Prefer the server-denormalized eventType; fall back to the shared ruleset for places written
  // before the denormalization migration.
  const eventType = place
    ? (place.eventType ?? resolvePlaceEventType(place.types ?? [], place.primaryType))
    : undefined;
  return eventType === 'place_dining' ? 'dining' : 'activity';
}

/** Stable sort that moves cancelled items to the end, keeping prior order. */
export function cancelledLast(items: ItineraryItem[]): ItineraryItem[] {
  return items.toSorted((a, b) => Number(a.isCancelled) - Number(b.isCancelled));
}

/**
 * Groups room items into hotel-stay batches: one batch per
 * (hotelId, checkInDate, checkOutDate). Same hotel on different dates is a
 * separate stay; different hotels are separate stays. Batches are sorted
 * earliest check-in first; rooms within each batch are sorted by per-room
 * cost (descending) to mirror the checkout page.
 *
 * Unlike {@link buildBookableGroups} this keeps every room — booked, cancelled
 * and unavailable included — because the bookings list shows the whole stay,
 * not just what's left to book.
 */
export function groupRoomsByStay(items: ItineraryItem[]): Array<{ key: string; items: ItineraryItem[] }> {
  const map = new Map<string, ItineraryItem[]>();
  for (const item of items) {
    if (item.type !== 'room' || !item.id) continue;
    const hotelId = item.roomOption?.hotelId ?? (item.referenceData as { hotelId?: string } | undefined)?.hotelId;
    const start = item.startDateTime?.slice(0, 10);
    const end = item.endDateTime?.slice(0, 10);
    const key = `${hotelId ?? 'unknown'}|${start ?? ''}|${end ?? ''}`;
    const list = map.get(key) ?? [];
    list.push(item);
    map.set(key, list);
  }
  return Array.from(map, ([key, batchItems]) => ({
    key,
    items: cancelledLast(batchItems.toSorted((a, b) => (b.cost?.amount ?? 0) - (a.cost?.amount ?? 0))),
  })).toSorted((a, b) => (a.items[0]?.startDateTime ?? '').localeCompare(b.items[0]?.startDateTime ?? ''));
}
